import './QuickAdd.scss';
import classNames from 'classnames';
import { COPY_GENERIC } from '@sonar-software/sonar-ts-constants';
import { Button, ButtonProps, InputProps } from 'antd';
import { InlineInputForm, InlineInputFormProps } from './InlineInputForm';
import React, { useEffect } from 'react';
import { useBoolean, useCurrentUser, usePrevious } from '@hooks';

export interface QuickAddProps extends Omit<InlineInputFormProps, 'onCancel' | 'currentValue'> {
	toggleProps?: ButtonProps;
	typeLabel: string;
	form?: React.FC<{ onCancel: () => void; processing?: boolean }>;
	noPadding?: boolean;
}

export const QuickAdd: React.FC<QuickAddProps> = ({
	form,
	inputProps,
	noPadding,
	processing,
	toggleProps,
	typeLabel,
	...rest
}) => {
	const { isReadOnly } = useCurrentUser();
	const [ isEditing, setIsEditing ] = useBoolean();
	const previousProcessing = usePrevious(processing, false);

	useEffect(() => {
		if (previousProcessing && !processing) {
			setIsEditing.off();
		}
	}, [ previousProcessing, processing, setIsEditing ]);

	if (isReadOnly) {
		return null;
	}

	if (!isEditing) {
		return (
			<div className={ classNames('quick-add-button-wrapper', { 'no-padding': noPadding }) }>
				<Button
					id='quick-add-button'
					{ ...toggleProps }
					onClick={ setIsEditing.on }
					type='primary'>
					{ COPY_GENERIC.CREATE_NEW(typeLabel) }
				</Button>
			</div>
		);
	}

	if (form) {
		const CustomFormComponent = form;

		return <CustomFormComponent
			onCancel={ setIsEditing.off }
			{ ...rest } />;
	}

	const modifiedInputProps: InputProps = {
		placeholder: COPY_GENERIC.NAME_YOUR(typeLabel),
		...inputProps
	};

	return (
		<InlineInputForm
			inputProps={ modifiedInputProps }
			noPadding={ noPadding }
			onCancel={ setIsEditing.off }
			{ ...rest } />
	);
};
